export type datePartDateTrunc =
    | 'day'
    | 'week'
    | 'month'
    | 'quarter'
    | 'year'
    | 'total';

export type datetimePartDateTrunc =
    | 'second'
    | 'minute'
    | 'hour'
    | 'day'
    | 'week'
    | 'month'
    | 'quarter'
    | 'year'
    | 'total';

export function getAllDatePartDateTrunc(): datePartDateTrunc[] {
    return ['day', 'week', 'month', 'quarter', 'year', 'total'];
}

/**
 * Generates a SQL `DATE_TRUNC` expression to truncate a date to the specified date part.
 *
 * @param {string} column - The column name or date expression to be truncated.
 * @param {'DAY'|'WEEK'|'MONTH'|'QUARTER'|'YEAR'} datePart - The part of the date to which the column should be truncated.
 *
 * @returns {string} - A SQL expression string using the `DATE_TRUNC` function.
 *
 * @example
 * sqlDateTrunc('created_at', 'WEEK');
 * // Returns: DATE_TRUNC(created_at, WEEK(MONDAY))
 *
 * @example
 * sqlDateTrunc('created_at', 'MONTH');
 * // Returns: DATE_TRUNC(created_at, MONTH)
 */
export function sqlDateTrunc(
    column: string,
    datePart: datePartDateTrunc
): string {
    if (datePart == 'total') {
        return `DATE('2000-01-01')`;
    }
    if (datePart == 'week') {
        return `DATE_TRUNC(${column}, WEEK(MONDAY))`;
    }
    return `DATE_TRUNC(${column}, ${datePart.toUpperCase()})`;
}

/**
 * Generates a SQL `DATETIME_TRUNC` expression to truncate a datetime to the specified datetime part.
 *
 * @param {string} datetimeColumn - The column name or datetime expression to be truncated.
 * @param {'DAY'|'WEEK'|'MONTH'|'QUARTER'|'YEAR'} datePart - The part of the datetime to which the column should be truncated.
 *
 * @returns {string} - A SQL expression string using the `DATETIME_TRUNC` function.
 *
 * @example
 * sqlDatetimeTrunc('created_at', 'WEEK');
 * // Returns: DATETIME_TRUNC(created_at, WEEK(MONDAY))
 *
 * @example
 * sqlDatetimeTrunc('created_at', 'MONTH');
 * // Returns: DATETIME_TRUNC(created_at, MONTH)
 */
export function sqlDatetimeTrunc(
    datetimeColumn: string,
    datetimePart: datetimePartDateTrunc
): string {
    if (datetimePart == 'total') {
        return `DATETIME('2000-01-01')`;
    }
    let internal = undefined;
    if (datetimePart == 'week') {
        internal = `DATETIME_TRUNC(${datetimeColumn}, WEEK(MONDAY))`;
    } else {
        internal = `DATETIME_TRUNC(${datetimeColumn}, ${datetimePart.toUpperCase()})`;
    }
    return internal;
    // TODO Po co ten switch?
    // switch (datetimePart) {
    //     case 'hour':
    //         return internal;
    //     default:
    //         return `CAST(${internal} AS DATE)`;
    // }
}

/**
 * Generates a SQL expression to format a datetime column for labeling purposes based on the specified datetime part.
 * @param datetimeColumn
 * @param datetimePart
 * @returns A SQL expression string that formats the datetime column.
 */
export function sqlDatetimeLabel(
    datetimeColumn: string,
    datetimePart: datetimePartDateTrunc
): string {
    switch (datetimePart) {
        case 'second':
            return `FORMAT_DATETIME('%Y-%m-%dT%H:%M:%S', ${datetimeColumn})`;
        case 'minute':
            return `FORMAT_DATETIME('%Y-%m-%dT%H:%M', ${datetimeColumn})`;
        case 'hour':
            return `FORMAT_DATETIME('%Y-%m-%dT%H', ${datetimeColumn})`;
        case 'day':
        case 'week':
            return `FORMAT_DATETIME('%Y-%m-%d', ${datetimeColumn})`;
        case 'month':
        case 'quarter':
            return `FORMAT_DATETIME('%Y-%m', ${datetimeColumn})`;
        case 'year':
            return `FORMAT_DATETIME('%Y', ${datetimeColumn})`;
        case 'total':
            return `'Total'`;
        default:
            return `FORMAT_DATETIME('%Y-%m-%dT%H', ${datetimeColumn})`;
    }
}
